import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { adminGetSupplierSettings, adminSaveSupplierSettings, adminTestSupplier } from '../../api/supplierSettings';
import Icon from '../../shared/components/Icons';
import AdminShell, { useAdminAccess } from './AdminShell';

const sourceLabel = { Admin: 'Saved here', AppSettings: 'From appsettings', NotSet: 'Not set' };
// Groups with a connection test, and its button label. The Email test sends a message to the signed-in admin.
const testLabels = { TripJack: 'Test connection', ETravelSmart: 'Test connection', Email: 'Send test email' };
const fmtWhen = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Draft per key: { value } for an edit, { reset: true } to go back to appsettings. Untouched keys have none. */
function changesFrom(drafts, settings) {
  return settings.flatMap((s) => {
    const draft = drafts[s.key];
    if (!draft) return [];
    if (draft.reset) return [{ key: s.key, reset: true }];
    const value = draft.value.trim();
    if (s.kind === 'Secret' ? value === '' : value === (s.value || '')) return [];
    return [{ key: s.key, value }];
  });
}

function SettingRow({ setting: s, draft, onChange }) {
  const [show, setShow] = useState(false);
  const secret = s.kind === 'Secret';
  const resetting = draft?.reset;
  const value = draft && !draft.reset ? draft.value : secret ? '' : s.value || '';
  const edited = Boolean(draft) && (resetting || (secret ? value.trim() !== '' : value.trim() !== (s.value || '')));
  return (
    <div className={`setting-row ${edited ? 'edited' : ''}`}>
      <div className="setting-meta">
        <label htmlFor={s.key}><b>{s.label}</b></label>
        <span className={`setting-source ${s.source.toLowerCase()}`}>{sourceLabel[s.source]}</span>
        {s.updatedAt && <span className="muted small">by {s.updatedBy || 'admin'} · {fmtWhen(s.updatedAt)}</span>}
        <p className="muted small">{s.description}</p>
      </div>
      <div className="setting-input">
        {resetting ? (
          <p className="api-message warning small">Will go back to the appsettings value when you save.</p>
        ) : (
          <span className="password-field">
            <input id={s.key} type={secret && !show ? 'password' : 'text'} value={value} autoComplete="off" spellCheck="false"
              inputMode={{ Digits: 'numeric', Url: 'url', Email: 'email' }[s.kind]}
              placeholder={secret ? (s.hasValue ? `Set${s.hint ? ` (${s.hint})` : ''} — type a new value to replace it` : 'Not set — enter a value') : ''}
              onChange={(e) => onChange({ value: e.target.value })} />
            {secret && (
              <button type="button" className="password-toggle" aria-label={show ? 'Hide value' : 'Show value'} onClick={() => setShow(!show)}>
                <Icon name={show ? 'eyeOff' : 'eye'} size={18} />
              </button>
            )}
          </span>
        )}
        <div className="setting-actions">
          {s.liveValue && !resetting && value !== s.liveValue && (
            <button type="button" className="link small" onClick={() => onChange({ value: s.liveValue })}>Use live: {s.liveValue}</button>
          )}
          {edited && <button type="button" className="link small" onClick={() => onChange(null)}>Undo</button>}
          {s.source === 'Admin' && !resetting && (
            <button type="button" className="link small" onClick={() => onChange({ reset: true })}>Reset to appsettings</button>
          )}
        </div>
      </div>
    </div>
  );
}

function TestResult({ result }) {
  if (!result) return null;
  if (result.loading) return <p className="muted small" role="status">Checking with the saved settings…</p>;
  if (result.error) return <p className="api-message error small" role="alert">{result.error}</p>;
  return (
    <ul className="setting-tests" role="status">
      {result.checks.map((c) => (
        <li key={c.name} className={c.ok ? 'ok' : 'fail'}><Icon name={c.ok ? 'check' : 'close'} size={14} /> <b>{c.name}</b> — {c.message}</li>
      ))}
    </ul>
  );
}

export default function AdminSupplierSettingsPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [groups, setGroups] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [tests, setTests] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasAccess) return undefined;
    let active = true;
    adminGetSupplierSettings(auth.accessToken)
      .then((data) => active && setGroups(data.groups))
      .catch((requestError) => active && setError(requestError.message));
    return () => { active = false; };
  }, [hasAccess, auth?.accessToken]);

  const all = groups ? groups.flatMap((g) => g.settings) : [];
  const changes = changesFrom(drafts, all);

  const setDraft = (key) => (draft) => {
    setMessage('');
    setDrafts((current) => {
      const next = { ...current };
      if (draft) next[key] = draft; else delete next[key];
      return next;
    });
  };

  const applyLiveUrls = (group) => {
    setMessage('');
    setDrafts((current) => ({
      ...current,
      ...Object.fromEntries(group.settings.filter((s) => s.liveValue).map((s) => [s.key, { value: s.liveValue }])),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!changes.length) return;
    if (!window.confirm(`Save ${changes.length} change${changes.length > 1 ? 's' : ''}? They apply immediately to new searches and bookings.`)) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const data = await adminSaveSupplierSettings(auth.accessToken, changes);
      setGroups(data.groups);
      setDrafts({});
      setTests({});
      setMessage('Settings saved and in use. Run "Test connection" to check the new values.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const test = async (group) => {
    setTests((t) => ({ ...t, [group]: { loading: true } }));
    try {
      const result = await adminTestSupplier(auth.accessToken, group);
      setTests((t) => ({ ...t, [group]: result }));
    } catch (requestError) {
      setTests((t) => ({ ...t, [group]: { error: requestError.message } }));
    }
  };

  return (
    <AdminShell title="Supplier settings"
      subtitle="Supplier API URLs and credentials, and the website URL. Saved values replace appsettings and apply without redeploying.">
      {error && <p className="api-message error" role="alert">{error}</p>}
      {message && <p className="api-message success" role="status">{message}</p>}
      {!groups && !error && <p role="status">Loading settings…</p>}
      {groups && (
        <form onSubmit={save} className="settings-form">
          <p className="api-message warning">
            Switching to live URLs and production keys makes real bookings that are charged to your supplier account.
            Secrets are stored encrypted and are never shown again after saving.
          </p>
          {groups.map((group) => (
            <section key={group.id} className="panel settings-group">
              <div className="settings-group-head">
                <div><h2>{group.title}</h2><p className="muted small">{group.description}</p></div>
                <div className="admin-actions">
                  {group.settings.some((s) => s.liveValue) && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => applyLiveUrls(group)}>Use all live URLs</button>
                  )}
                  {testLabels[group.id] && (
                    <button type="button" className="btn btn-outline btn-sm" disabled={tests[group.id]?.loading}
                      title={changes.length ? 'Tests the saved settings; save your changes first.' : undefined}
                      onClick={() => test(group.id)}>{testLabels[group.id]}</button>
                  )}
                </div>
              </div>
              <TestResult result={tests[group.id]} />
              {group.settings.map((s) => <SettingRow key={s.key} setting={s} draft={drafts[s.key]} onChange={setDraft(s.key)} />)}
            </section>
          ))}
          <div className="settings-save">
            <span className="muted small">{changes.length ? `${changes.length} unsaved change${changes.length > 1 ? 's' : ''}` : 'No changes'}</span>
            <button type="button" className="btn btn-ghost" disabled={!Object.keys(drafts).length || busy} onClick={() => setDrafts({})}>Discard</button>
            <button className="btn btn-primary" disabled={!changes.length || busy}>{busy ? 'Saving…' : 'Save changes'}</button>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
